import type { Loop, Preferences } from "./types";

const DAY = 86_400_000;

export function formatDate(value?: string | null, prefs?: Preferences): string {
  if (!value) return "No date set";
  const d = new Date(value);
  const fmt = prefs?.dateFormat ?? "MMM d, yyyy";
  if (fmt === "yyyy-MM-dd") return d.toISOString().slice(0, 10);
  const opts: Intl.DateTimeFormatOptions =
    fmt === "d MMM yyyy"
      ? { day: "numeric", month: "short", year: "numeric" }
      : { month: "short", day: "numeric", year: "numeric" };
  return new Intl.DateTimeFormat("en-GB", opts).format(d);
}

export function formatLongDate(value?: string | null): string {
  if (!value) return "No date set";
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(value));
}

export function relativeDay(value?: string | null): string {
  if (!value) return "No date";
  const diff = Math.round((new Date(value).getTime() - Date.now()) / DAY);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1) return `In ${diff} days`;
  return `${Math.abs(diff)} days ago`;
}

export function timeOpen(loop: Loop): string {
  const days = Math.max(0, Math.round((Date.now() - new Date(loop.createdAt).getTime()) / DAY));
  if (days === 0) return "Open today";
  if (days === 1) return "Open 1 day";
  if (days < 30) return `Open ${days} days`;
  const months = Math.round(days / 30);
  return `Open ${months} month${months === 1 ? "" : "s"}`;
}

export function isOverdue(loop: Loop): boolean {
  if (!loop.expectedDate || loop.status === "resolved" || loop.status === "archived") return false;
  return new Date(loop.expectedDate).getTime() < Date.now();
}

export function groupByRecency<T extends { updatedAt: string }>(items: T[]) {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const buckets: { label: string; items: T[] }[] = [
    { label: "Today", items: [] },
    { label: "Yesterday", items: [] },
    { label: "Previous 7 Days", items: [] },
    { label: "Older", items: [] },
  ];
  for (const item of items) {
    const t = new Date(item.updatedAt).getTime();
    const daysAgo = Math.floor((startOfToday.getTime() - t) / DAY);
    if (t >= startOfToday.getTime()) buckets[0]!.items.push(item);
    else if (daysAgo < 1) buckets[1]!.items.push(item);
    else if (daysAgo < 7) buckets[2]!.items.push(item);
    else buckets[3]!.items.push(item);
  }
  return buckets.filter((b) => b.items.length > 0);
}
